import { useState } from 'react'

import { api } from '../api/client'
import type { EagleWallLink, EagleWallLinkCreated, EagleWallLinkIn, WallMonitor } from '../api/types'
import { useAsync } from '../hooks/useAsync'
import { t } from '../i18n/lingua'
import { Card, EmptyState, ErrorBanner, Spinner, formatDateTime } from './common'
import { DataTable, type Column } from './DataTable'

/**
 * «Link per i monitor»: gli indirizzi che aprono il sinottico di Eagle
 * **senza accesso** (`/monitor/eagle/<token>`), per gli schermi a parete.
 *
 * Un monitor in corridoio non ha nessuno che faccia il login, e un utente
 * condiviso su uno schermo è la peggiore delle credenziali. Il link vale
 * solo in lettura per il sinottico (non apre la console), solo dagli
 * indirizzi IP che l'amministratore elenca, può indicare quale dashboard
 * mostrare e con quale titolo, e si revoca con un clic. Il segreto si
 * mostra una volta sola. Solo gli amministratori: il server rifiuta gli
 * altri (`require_tenant_admin`), e una chiave API del tenant non può
 * crearne (denylist in `deps.py`).
 *
 * I link che il Control Plane conia per i **monitor accoppiati** (CENSO
 * Monitor) non si creano né si toccano qui: stanno in un elenco a sé, in
 * sola lettura, perché si rinnovano da soli, seguono l'indirizzo da cui il
 * monitor batte e si cambiano dalla riga del monitor («Link nuovo»).
 * L'indirizzo che il Control Plane vede è quello pubblico del sito (dietro
 * un NAT è lo stesso per tutti i monitor): accanto c'è quello vero del
 * monitor, come lo dichiara lui.
 */
interface Bozza {
  name: string
  title: string
  dashboard_id: string
  /** One address or CIDR per line. */
  ips: string
  /** yyyy-mm-dd, empty = never. */
  scadenza: string
  /** '' = the list; 'host' | 'net' = lock at the first use (a monitor on DHCP). */
  blocco: '' | 'host' | 'net'
}

const VUOTA: Bozza = { name: '', title: '', dashboard_id: '', ips: '', scadenza: '', blocco: '' }

function bozzaDi(l: EagleWallLink): Bozza {
  return {
    name: l.name, title: l.title ?? '', dashboard_id: l.dashboard_id ?? '', ips: l.allowed_ips.join('\n'),
    scadenza: l.expires_at ? l.expires_at.slice(0, 10) : '',
    blocco: l.pin_first_use ?? '',
  }
}

function corpo(b: Bozza): EagleWallLinkIn {
  return {
    name: b.name.trim(),
    title: b.title.trim() || null,
    dashboard_id: b.dashboard_id || null,
    allowed_ips: b.ips.split(/[\n,;]+/).map((x) => x.trim()).filter(Boolean),
    expires_at: b.scadenza ? new Date(`${b.scadenza}T23:59:59`).toISOString() : null,
    pin_first_use: b.blocco || null,
  }
}

export function LinkMonitor() {
  const dati = useAsync(() => api.wallLinks(), [])
  // The cockpits a monitor may show (a wall display saved before migration
  // 0217 is a cockpit now; a console not yet updated may still list some).
  const dashboards = useAsync(async () => {
    const [cruscotti, pareti] = await Promise.all([api.dashboards('eagle'), api.dashboards('eagle_wall').catch(() => ({ dashboards: [] }))])
    return { dashboards: [...cruscotti.dashboards, ...pareti.dashboards] }
  }, [])
  const [aperto, setAperto] = useState<EagleWallLink | 'nuovo' | null>(null)
  const [bozza, setBozza] = useState<Bozza>(VUOTA)
  const [busy, setBusy] = useState(false)
  const [errore, setErrore] = useState<unknown>(null)
  const [creato, setCreato] = useState<EagleWallLinkCreated | null>(null)
  // The paired monitors, for their links' table: the monitor's own address.
  const monitorAccoppiati = useAsync(() => api.wallMonitors().then((d) => d.monitors).catch(() => [] as WallMonitor[]), [])
  const perMonitor = new Map((monitorAccoppiati.data ?? []).map((m) => [m.id, m]))
  const tutti = dati.data?.links ?? []
  const manuali = tutti.filter((l) => !l.monitor_id)
  const deiMonitor = tutti.filter((l) => l.monitor_id)

  function apri(che: EagleWallLink | 'nuovo') {
    setAperto(che)
    setBozza(che === 'nuovo' ? VUOTA : bozzaDi(che))
    setErrore(null)
  }

  async function salva(e: React.FormEvent) {
    e.preventDefault()
    if (!aperto) return
    setBusy(true)
    setErrore(null)
    try {
      if (aperto === 'nuovo') {
        setCreato(await api.wallLinkCrea(corpo(bozza)))
      } else {
        await api.wallLinkAggiorna(aperto.id, corpo(bozza))
      }
      setAperto(null)
      dati.reload()
    } catch (err) {
      setErrore(err)
    } finally {
      setBusy(false)
    }
  }

  async function revoca(l: EagleWallLink) {
    if (!confirm(t('Revocare il link «{name}»? Il monitor che lo usa resta a schermo vuoto.', { name: l.name }))) return
    await api.wallLinkRevoca(l.id)
    dati.reload()
  }
  // The same call on a link already stopped removes it for good.
  async function elimina(l: EagleWallLink) {
    if (!confirm(t('Eliminare il link «{name}»? Sparisce dall’elenco, con il suo ultimo uso.', { name: l.name }))) return
    await api.wallLinkRevoca(l.id)
    dati.reload()
  }

  const url = creato ? `${window.location.origin}${creato.path}` : ''
  const senzaIp = corpo(bozza).allowed_ips.length === 0 && !bozza.blocco
  const colonne: Column<EagleWallLink>[] = [
    { key: 'name', header: t('Nome'), render: (l) => <strong style={{ opacity: l.active ? 1 : 0.55 }}>{l.name}</strong> },
    { key: 'title', header: t('Titolo sul monitor'), value: (l) => l.title ?? '', render: (l) => l.title ?? <span className="muted">{t('il nome della dashboard')}</span> },
    { key: 'dashboard', header: t('Cruscotto mostrato'), value: (l) => l.dashboard_name ?? '', render: (l) => l.dashboard_name ?? <span className="muted">{t('quello del tenant')}</span> },
    { key: 'ips', header: t('IP autorizzati'), mono: true, value: (l) => l.allowed_ips.join(' ') || (l.pin_first_use ?? ''), render: (l) => l.allowed_ips.length ? l.allowed_ips.join(', ') : l.pin_first_use ? <span className="muted">{l.pin_first_use === 'net' ? t('la rete del primo uso') : t('l’indirizzo del primo uso')}</span> : <span className="muted">{t('qualsiasi indirizzo')}</span> },
    { key: 'last_used_at', header: t('Ultimo uso'), type: 'date', value: (l) => l.last_used_at ?? '', render: (l) => l.last_used_at ? <>{formatDateTime(l.last_used_at)}{l.last_ip && <span className="muted"> · {l.last_ip}</span>}</> : <span className="muted">{t('mai')}</span> },
    { key: 'expires_at', header: t('Scadenza'), type: 'date', value: (l) => l.expires_at ?? '', render: (l) => l.expires_at ? formatDateTime(l.expires_at) : <span className="muted">{t('nessuna')}</span> },
    { key: 'active', header: t('Stato'), value: (l) => l.revoked_at ? t('revocato') : l.active ? t('attivo') : t('scaduto') },
    { key: 'azioni', header: '', sort: false, filter: false, searchable: false, render: (l) => (
      <span className="rowactions" style={{ display: 'inline-flex', gap: 6 }}>
        {l.active ? (
          <>
            <button type="button" className="secondary" onClick={() => apri(l)}>{t('Modifica')}</button>
            <button type="button" className="secondary" onClick={() => void revoca(l)}>{t('Revoca')}</button>
          </>
        ) : (
          <button type="button" className="secondary" onClick={() => void elimina(l)}>{t('Elimina')}</button>
        )}
      </span>
    ) },
  ]

  const colonneMonitor: Column<EagleWallLink>[] = [
    { key: 'monitor', header: t('Monitor'), value: (l) => perMonitor.get(l.monitor_id ?? '')?.name ?? l.name, render: (l) => {
      const m = perMonitor.get(l.monitor_id ?? '')
      return <><strong style={{ opacity: l.active ? 1 : 0.55 }}>{m?.name ?? l.name}</strong>{m?.hostname && <><br /><small className="muted mono">{m.hostname}</small></>}</>
    } },
    { key: 'dashboard', header: t('Cruscotto mostrato'), value: (l) => l.dashboard_name ?? '', render: (l) => <>{l.dashboard_name ?? <span className="muted">{t('quello del tenant')}</span>}{l.title && <><br /><small className="muted">{l.title}</small></>}</> },
    { key: 'indirizzo', header: t('Indirizzo del monitor'), mono: true, value: (l) => perMonitor.get(l.monitor_id ?? '')?.state.network?.ip ?? '', render: (l) => {
      const m = perMonitor.get(l.monitor_id ?? '')
      return m?.state.network?.ip ? <>{m.state.network.ip}{m.state.network.iface && <span className="muted"> ({m.state.network.iface})</span>}</> : <span className="muted">—</span>
    } },
    { key: 'ips', header: t('Vale da'), mono: true, value: (l) => l.allowed_ips.join(' '), render: (l) => l.allowed_ips.length
      ? <>{l.allowed_ips.join(', ')}<br /><small className="muted">{t('come il Control Plane lo vede: dietro un NAT è l’indirizzo pubblico del sito')}</small></>
      : <span className="muted">{t('qualsiasi indirizzo, fino al prossimo battito del monitor')}</span> },
    { key: 'last_used_at', header: t('Ultimo uso'), type: 'date', value: (l) => l.last_used_at ?? '', render: (l) => l.last_used_at ? <>{formatDateTime(l.last_used_at)}{l.last_ip && <span className="muted"> · {l.last_ip}</span>}</> : <span className="muted">{t('mai')}</span> },
    { key: 'expires_at', header: t('Rinnovo'), type: 'date', value: (l) => l.expires_at ?? '', render: (l) => l.expires_at ? <span className="muted">{t('da solo, prima del {d}', { d: formatDateTime(l.expires_at) })}</span> : <span className="muted">{t('nessuna')}</span> },
    { key: 'active', header: t('Stato'), value: (l) => l.revoked_at ? t('revocato') : l.active ? t('attivo') : t('scaduto') },
  ]

  return (
    <Card title={t('Link per i monitor')} actions={(
      <button type="button" onClick={() => apri('nuovo')} disabled={aperto === 'nuovo'}>{t('Nuovo link')}</button>
    )}>
      <p className="field__hint">
        {t('Un monitor a parete: un link che mostra il cruscotto scelto senza accesso, in sola lettura per Eagle (non apre la console), solo dagli indirizzi IP che autorizzi, revocabile con un clic. Il segreto si mostra una volta sola.')}
      </p>
      <ErrorBanner error={dati.error} />

      {creato && (
        <div className="eagle-blocco" style={{ marginBottom: 12 }}>
          <strong>{t('Link creato: copialo adesso, non sarà più visibile.')}</strong>
          <div className="linkmon__url">
            <code>{url}</code>
            <button type="button" className="secondary" onClick={() => { void navigator.clipboard?.writeText(url) }}>{t('Copia')}</button>
            <button type="button" className="secondary" onClick={() => setCreato(null)}>{t('Chiudi')}</button>
          </div>
          <p className="field__hint">{t('Aprilo nel browser del monitor, in modalità chiosco o a schermo intero: il sinottico parte da solo e si aggiorna da sé.')}</p>
        </div>
      )}

      {aperto && (
        <form className="form-dialog" onSubmit={(e) => void salva(e)} style={{ marginBottom: 14 }}>
          <div className="form-grid">
            <label className="field span-2"><span className="field__label">{t('Nome')}</span>
              <input type="text" required value={bozza.name} onChange={(e) => setBozza((b) => ({ ...b, name: e.target.value }))} placeholder={t('es. NOC, corridoio piano 2')} /></label>
            <label className="field span-2"><span className="field__label">{t('Titolo sul monitor')}</span>
              <input type="text" value={bozza.title} onChange={(e) => setBozza((b) => ({ ...b, title: e.target.value }))} placeholder={t('il nome della dashboard')} /></label>
            <label className="field span-2"><span className="field__label">{t('Cruscotto da mostrare')}</span>
              <select value={bozza.dashboard_id} onChange={(e) => setBozza((b) => ({ ...b, dashboard_id: e.target.value }))}>
                <option value="">{t('quello del tenant (o la disposizione da parete predefinita)')}</option>
                {(dashboards.data?.dashboards ?? []).map((d) => d.id && (
                  <option key={d.id} value={d.id}>{d.name}{d.shared ? ` · ${t('del tenant')}` : ` · ${t('personale')}`}</option>
                ))}
              </select></label>
            <label className="field span-2"><span className="field__label">{t('Indirizzi autorizzati')}</span>
              <select value={bozza.blocco} onChange={(e) => setBozza((b) => ({ ...b, blocco: e.target.value as Bozza['blocco'] }))}>
                <option value="">{t('quelli elencati qui sotto')}</option>
                <option value="net">{t('la rete /24 del primo uso (monitor in DHCP)')}</option>
                <option value="host">{t('l’indirizzo del primo uso')}</option>
              </select>
              <span className="field__hint">{bozza.blocco === 'net'
                ? t('Il link vale da ovunque finché non viene aperto la prima volta: da allora solo dalla rete /24 di quel monitor, anche se il DHCP gli cambia indirizzo.')
                : bozza.blocco === 'host' ? t('Dal primo uso in poi vale solo da quell’indirizzo.')
                  : t('Un monitor in DHCP: indica la rete della sua subnet (es. 192.168.10.0/24) o scegli il blocco al primo uso.')}</span></label>
            <label className="field span-2"><span className="field__label">{t('IP autorizzati (uno per riga, anche reti come 192.168.10.0/24)')}</span>
              <textarea rows={3} value={bozza.ips} onChange={(e) => setBozza((b) => ({ ...b, ips: e.target.value }))} placeholder={'10.0.0.5\n192.168.10.0/24'} spellCheck={false} /></label>
            <label className="field span-2"><span className="field__label">{t('Scadenza (facoltativa)')}</span>
              <input type="date" value={bozza.scadenza} onChange={(e) => setBozza((b) => ({ ...b, scadenza: e.target.value }))} /></label>
          </div>
          {senzaIp && (
            <p className="field__hint" style={{ color: 'var(--warn)' }}>
              {t('Senza indirizzi il link vale da ovunque: chiunque lo abbia vede il monitoraggio. Meglio elencare gli IP dei monitor.')}
            </p>
          )}
          <ErrorBanner error={errore} />
          <div className="form-dialog__azioni">
            <button type="submit" disabled={busy || !bozza.name.trim()}>{busy ? t('Salvo…') : aperto === 'nuovo' ? t('Crea link') : t('Salva')}</button>
            <button type="button" className="secondary" onClick={() => setAperto(null)} disabled={busy}>{t('Annulla')}</button>
          </div>
        </form>
      )}

      {dati.loading && !dati.data ? <Spinner /> : dati.data && manuali.length === 0 ? (
        <EmptyState title={t('Nessun link creato a mano.')} hint={deiMonitor.length > 0
          ? t('I monitor con CENSO Monitor hanno il loro link, qui sotto. «Nuovo link» serve a uno schermo senza il programma.')
          : t('«Nuovo link» ne crea uno: lo apri sul monitor e il sinottico parte senza accesso.')} />
      ) : dati.data ? (
        <DataTable rows={manuali} columns={colonne} rowKey={(l) => l.id} conteggio={{ nome: t('link') }} />
      ) : null}

      {deiMonitor.length > 0 && (
        <details className="linkmon__monitor" style={{ marginTop: 14 }}>
          <summary>{t('{n} link tenuti dai monitor accoppiati', { n: deiMonitor.length })}</summary>
          <p className="field__hint">
            {t('Li conia e li rinnova il Control Plane, uno per monitor, e non si modificano qui: valgono solo dall’indirizzo da cui il monitor batte (lo seguono se cambia) e si rifanno da soli prima della scadenza. Per cambiarne uno usa «Link nuovo» sulla riga del monitor; per fermarlo, «Scollega».')}
          </p>
          <DataTable rows={deiMonitor} columns={colonneMonitor} rowKey={(l) => l.id} conteggio={{ nome: t('link') }} />
        </details>
      )}
    </Card>
  )
}
